/** Serverzustand fuer eigene Felder. */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/apiError';
import {
  createCustomField,
  deleteCustomField,
  listCustomFields,
  suggestCustomFieldValues,
  updateCustomField,
  type CustomFieldObjectType,
} from './api';

export const customFieldKeys = {
  all: ['customfields'] as const,
  list: (objectType?: CustomFieldObjectType, onlyActive = false) =>
    ['customfields', objectType ?? 'all', onlyActive] as const,
};

export function useCustomFields(objectType?: CustomFieldObjectType, onlyActive = false) {
  return useQuery({
    queryKey: customFieldKeys.list(objectType, onlyActive),
    queryFn: () => listCustomFields(objectType, onlyActive),
    staleTime: 5 * 60 * 1000,
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.isPermanent) && failureCount < 2,
  });
}

/** Vorschlaege fuer ein eigenes Textfeld -- Etappe 3 der Autovervollstaendigung. */
export function useCustomFieldSuggestions(definitionId: string, term: string, enabled: boolean) {
  return useQuery({
    queryKey: ['customfields', 'suggestions', definitionId, term],
    queryFn: () => suggestCustomFieldValues(definitionId, term),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    // Ein 403 heisst "keine Vorschlaege fuer diese Rolle" -- nicht wiederholen.
    retry: false,
  });
}

export function useCreateCustomField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => createCustomField(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: customFieldKeys.all });
    },
  });
}

export function useUpdateCustomField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Record<string, unknown> }) =>
      updateCustomField(id, payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: customFieldKeys.all });
    },
  });
}

export function useDeleteCustomField() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCustomField(id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: customFieldKeys.all });
    },
  });
}
